/**
 * Type Mappers
 *
 * Functions to convert between backend (pure-js) types and frontend (apiLayer) types
 */

import { User as BackendUser } from '../../../../pure-js/types/User'
import { User } from '../types'
import { OnboardingChapter } from '../../../../pure-js/enums/OnboardingChapter'
import { PlatformTourOutcome } from '../../../../pure-js/enums/PlatformTourOutcome'
import { DemographicAnswers } from '../../../../pure-js/types/Demographics'
import { Team as BackendTeam } from '../../../../pure-js/types/Team'
import { CreateTeamRequest } from '../../../../pure-js/types/types'
import { State } from '../../hooks/useAppState'

/**
 * Backend User Update Type
 * Represents the fields that can be updated on the backend User
 */
export type BackendUserUpdate = Partial<{
  displayName: string
  email: string
  phone: string
  profileImage: string
  language: string
  completedOnboardingSteps: OnboardingChapter[]
  currentAssessmentQuestion: number | undefined
  privacyPolicyAgree: boolean
  lastSessionId: string
  team: string
  demographics: DemographicAnswers
  preferences: BackendUser['preferences']
  tasks: {
    latestSessionFeedback?: {
      sessionId: string
      at: string
    }
    profileFeedback?: {
      sessionIds: string[]
      at: string
    }
    platformTour?: {
      outcome: PlatformTourOutcome
      at: string
    }
  }
}>

/**
 * Questionnaire Context Type
 * Defines valid questionnaire contexts
 */
export type QuestionnaireContext = 'preassessment' | 'postassessment' | 'reflection'

/**
 * Mapper to convert backend User to frontend User
 *
 * Maps fields like:
 * - displayName → name
 * - profileImage → avatar
 *
 * @param backendUser - User object from backend/pure-js
 * @returns User object in frontend format
 */
export const mapBackendUserToFrontendUser = (backendUser: BackendUser): User => {
  return {
    ...backendUser,
    id: backendUser.id,
    email: backendUser.email,
    name: backendUser.displayName || backendUser.email,
    avatar: backendUser.profileImage,
    phone: backendUser.phone,
    createdAt: backendUser.createdAt || new Date().toISOString(),
    updatedAt: backendUser.updatedAt || new Date().toISOString()
  }
}

/**
 * Mapper to convert frontend User to backend User payload
 *
 * Maps fields like:
 * - name → displayName
 * - avatar → profileImage
 *
 * Also passes through backend-only fields like completedOnboardingSteps
 *
 * Only includes fields that are defined (for partial updates)
 *
 * @param frontendUser - User object from frontend (may include backend fields via 'as any')
 * @returns User object in backend format (partial)
 */
export const mapFrontendUserToBackendUser = (
  frontendUser: Partial<User> & Record<string, unknown>
): Partial<BackendUser> => {
  const payload: Partial<BackendUser> = {}

  // Frontend → Backend field mappings
  if (frontendUser.name !== undefined) {
    payload.displayName = frontendUser.name
  }
  if (frontendUser.avatar !== undefined) {
    payload.profileImage = frontendUser.avatar
  }
  if (frontendUser.phone !== undefined) {
    payload.phone = frontendUser.phone
  }
  if (frontendUser.email !== undefined) {
    payload.email = frontendUser.email
  }

  // Backend-only fields (pass through directly)
  if (frontendUser.completedOnboardingSteps !== undefined) {
    payload.completedOnboardingSteps = frontendUser.completedOnboardingSteps as OnboardingChapter[]
  }
  if (frontendUser.currentAssessmentQuestion !== undefined) {
    payload.currentAssessmentQuestion = frontendUser.currentAssessmentQuestion as number | undefined
  }
  if (frontendUser.displayName !== undefined) {
    payload.displayName = frontendUser.displayName as string | undefined
  }
  if (frontendUser.privacyPolicyAgree !== undefined) {
    payload.privacyPolicyAgree = frontendUser.privacyPolicyAgree as boolean | undefined
  }
  if (frontendUser.lastSessionId !== undefined) {
    payload.lastSessionId = frontendUser.lastSessionId as string | undefined
  }

  if (frontendUser.tasks !== undefined) {
    payload.tasks = frontendUser.tasks as BackendUser['tasks']
  }
  if (frontendUser.demographics !== undefined) {
    payload.demographics = frontendUser.demographics as DemographicAnswers
  }
  if (frontendUser.preferences !== undefined) {
    payload.preferences = frontendUser.preferences as BackendUser['preferences']
  }

  return payload
}

/**
 * Mapper to convert CreateTeamRequest to Team
 *
 * @param request - CreateTeamRequest from frontend
 * @param teamId - Generated team ID
 * @param state - App state for createdBy
 * @returns Team object for backend
 */
export const mapCreateTeamRequestToTeam = (request: CreateTeamRequest, teamId: string, state: State): BackendTeam => {
  return {
    ...request,
    id: teamId,
    code: `TEAM-${teamId.slice(5, 13).toUpperCase()}`,
    createdBy: state.user?.id || '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    metaData: {}
  } as BackendTeam
}
